import {useQuery} from "@apollo/client";
import map from "lodash/map";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FilterCategory, FilterCategoryType, FilterSets} from "@/components/filterDropdown/FilterDropdown.ts";
import {FilterDropdown} from "@/components/filterDropdown/FilterDropdown.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {AlertFilteringDataResult, GQL_MONITORING_GROUPS_AND_TAGS_FILTER_DATA} from "@/lib/queries/alerting.ts";
import {getCheckTypeOptions} from "@/lib/types/checks.ts";

type CheckFilterProps = {
  filterSets: FilterSets<FilterCategory>;
  onFilterSetChange: (filterSet: FilterSets<FilterCategory>) => void;
  portal?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
};

export function ChecksFilters({filterSets, onFilterSetChange, portal, disabled, disabledTooltip}: CheckFilterProps) {
  const {data, loading} = useQuery<AlertFilteringDataResult>(GQL_MONITORING_GROUPS_AND_TAGS_FILTER_DATA);
  const {user} = useCurrentUserContext();
  const checkTypeOptions = getCheckTypeOptions(user?.account?.feature_flags ? user.account.feature_flags : []);

  const {tag_names, ...baseFilters} = filterSets;
  const appliedFilterSets = {
    // Until the tags arrive there are no ids to map the URL's tag names onto, so the names stand in
    // and the applied count is right from the first paint. FilterDropdown keeps the actions disabled
    // while loading, so these placeholders cannot be applied.
    ...(Array.isArray(tag_names) && {
      tags: data
        ? tag_names
            .map((tagName) => data.tags.find(({tag}) => tag === tagName)?.id.toString())
            .filter((id) => id !== undefined)
        : tag_names,
    }),
    ...baseFilters,
  };

  // Built unconditionally: only the options depend on the query, and gating the whole array on it
  // left the toggle with nothing to count, so the applied total jumped once the data landed.
  const filterFields: FilterCategory[] = [
    {
      type: FilterCategoryType.ARRAY,
      key: "locations",
      display: "Locations",
      options: (data?.monitoringGroups ?? []).map((monitoringGroup) => ({
        key: monitoringGroup.id.toString(),
        display: monitoringGroup.location,
      })),
      sorted: true,
    },
    {
      type: FilterCategoryType.ARRAY,
      key: "tags",
      display: "Tags",
      options: (data?.tags ?? []).map((tag) => ({
        key: tag.id.toString(),
        display: tag.tag,
        color_hex: tag.color_hex,
      })),
      sorted: true,
      matchMode: {enabled: true},
    },
    {
      type: FilterCategoryType.ARRAY,
      key: "contact_groups",
      display: "Contact Group",
      options: (data?.contactGroups.items ?? []).map((contactGroup) => ({
        key: contactGroup.id.toString(),
        display: contactGroup.name,
      })),
      sorted: true,
      matchMode: {enabled: true},
    },
    {
      type: FilterCategoryType.ARRAY,
      key: "monitoring_service_type",
      display: "Check Type",
      options: map(checkTypeOptions, (value, key) => {
        return {
          key: key,
          display: value,
        };
      }),
      sorted: true,
    },
    {
      type: FilterCategoryType.ARRAY,
      key: "status",
      display: "Status",
      options: [
        {
          key: "up",
          display: "Up",
        },
        {
          key: "down",
          display: "Down",
        },
        {
          key: "paused",
          display: "Paused",
        },
        {
          key: "maintenance",
          display: "Maintenance",
        },
      ],
    },
  ];

  const dropdown = (
    <FilterDropdown
      defaultActiveCategoryId="locations"
      categories={filterFields}
      appliedFilterSets={appliedFilterSets}
      onApply={onFilterSetChange}
      loading={loading}
      portal={portal}
      disabled={disabled}
    />
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip tooltipContent={disabledTooltip} placement="right" onModal={true}>
        <span>{dropdown}</span>
      </Tooltip>
    );
  }

  return dropdown;
}
